import {
  AudioType,
  Design,
  LanguageCode,
  Option,
  Public,
  WorkflowLanguage,
  WorkflowObject,
  WorkflowPromptSubmission,
  WorkflowPromptSubmissions,
  WorkflowVoice
} from '@app/types';
import { pick, sortByProp } from '@app/utilities';
import { workflowStatusMapping } from './const';
import { IHttpService } from './http-service';
import {
  Cache,
  HttpMethod,
  WorkflowPromptSubmissionsRaw,
  WorkflowPromptVoiceStatus
} from './types';

/** Public interface of @see WorkflowService. */
export type IWorkflowService = Public<WorkflowService>;

export type WorkflowPromptStatus = {
  voices?: {
    audioUrl: string;
    status: WorkflowPromptVoiceStatus;
    text: string;
    voice: string;
  }[];
};

type VoiceOption = (
  Option<string>
  & Pick<WorkflowVoice, 'description' | 'vendor'>
  & {
    languages: {
      label?: string;
      code: LanguageCode;
    }[];
  }
);

type CacheType = (
  Record<`voices/${AudioType}/client/${string}`, ReturnType<WorkflowService['getVoices']>>
  & {
    languages: ReturnType<WorkflowService['getLanguages']>;
  }
);

export class WorkflowService {
  /** Base URL to add to all HTTP requests. */
  static readonly baseUrl = '/workflow';
  private _cache = new Cache<CacheType>();
  private _httpService: IHttpService;

  constructor(httpService: IHttpService) {
    this._httpService = httpService;
  }

  async getLanguages(): Promise<Option<LanguageCode>[]> {
    return this._cache.getItem('languages', async () => {
      const { value: languages = [] } = await this._httpService.fetchJson<WorkflowLanguage[]>({
        url: 'languages'
      });

      return languages.sort(sortByProp('languageName')).map(({ languageName, localeCode }) => {
        return {
          label: languageName,
          value: this._convertLanguageCode(localeCode)
        };
      });
    });
  }

  async getPromptStatus(
    promptName: string,
    {
      applicationVersion,
      clientName,
      designName
    }: Design
  ): Promise<WorkflowPromptStatus | null> {
    const url = [
      'prompt/getstatus/client',
      clientName,
      'application',
      clientName + designName,
      'version',
      applicationVersion,
      promptName
    ].join('/');
    return (await this._httpService.fetchJson<WorkflowPromptStatus>({
      method: HttpMethod.get,
      url
    })).value ?? null;
  }

  async getVoices(
    audioType?: AudioType,
    languageCode?: LanguageCode,
    serialized = window.apm.serialized
  ): Promise<VoiceOption[]> {
    if (!audioType) {
      return [
        ...await this.getVoices(AudioType.ttp),
        ...await this.getVoices(AudioType.tts)
      ].sort(sortByProp('label'));
    }

    const url = `voices/${audioType}/client/${serialized.clientName}` as const;
    return (await this._cache.getItem(url, async () => {
      const voices = await this._httpService.fetchJsonOrFail<WorkflowVoice[]>({ url });

      return voices.map((voice): VoiceOption => {
        return {
          ...pick(voice, ['description', 'label', 'vendor']),
          languages: voice.languages.map(({ languageName, localeCode }) => ({
            code: this._convertLanguageCode(localeCode),
            label: languageName
          })),
          value: voice.internalCode
        };
      }).sort(sortByProp('label'));
    })).filter((voice) => {
      return (
        !languageCode
        || voice.languages.some(({ code }) => code === languageCode)
      );
    });
  };

  /** Get the latest prompt submissions (if any) for the current @see Design.designId. */
  async getPromptSubmissions({
    clientName,
    designName
  }: Design): Promise<WorkflowPromptSubmissions> {
    return Object.entries(
      await this._httpService.fetchJsonOrFail<WorkflowPromptSubmissionsRaw>({
        method: HttpMethod.get,
        url: `project/activation/${clientName}/${clientName}${designName}`
      })
    ).map(([version, {
      activatedModule,
      currentStateMessage,
      details,
      status
    }]): WorkflowPromptSubmission => {
      const {
        activationDate,
        author: { fullName: author } = { fullName: null }
      } = activatedModule ?? {};

      return {
        activatedOn: (activationDate) ? new Date(activationDate) : null,
        author,
        message: details?.map(({ message }) => {
          return message;
        }).join('. ') ?? currentStateMessage ?? null,
        status: workflowStatusMapping[status],
        version
      };
    });
  }

  async updatePrompts(workflowObject: WorkflowObject): Promise<Response> {
    return await this._httpService.fetch({
      body: workflowObject,
      method: HttpMethod.post,
      url: 'project/activation'
    });
  }

  /** Convert localeCode from WFS to D2 @see LanguageCode. */
  private _convertLanguageCode(localeCode: string): LanguageCode {
    const [languageCode, countryCode] = localeCode.split('-');
    return `${languageCode}-${countryCode.toUpperCase()}` as LanguageCode;
  }
}
